export type BeLoginResponse =
  | {
      ok: true;
      token: string;
      user: {
        id: string;
        username: string;
        nickname?: string;
        gender?: "M" | "F";
        permission?: "user" | "admin" | "agent";
        usdtWalletAddress?: string;
        balance: number;
        characterImageNo?: number;
      };
    }
  | { ok: false; error: string; message?: string };

export type BeRegisterResponse =
  | {
      ok: true;
      user: {
        id: string;
        username: string;
        nickname?: string;
        gender?: "M" | "F";
        permission?: "user" | "admin" | "agent";
        usdtWalletAddress?: string;
        balance: number;
        characterImageNo?: number;
        /** Admin 사용자관리와 동일: PENDING | ACTIVE | CANCELLED | BLOCKED */
        state?: string;
      };
    }
  | { ok: false; error: string; message?: string };

/**
 * HTTPS 페이지에서 같은 호스트로만 열린 http:// API 베이스는 Mixed Content 로 차단되므로 https 로 올린다.
 * (로컬에서 페이지는 https·BE는 http 다른 호스트인 경우는 hostname 이 달라 그대로 둔다.)
 */
function alignBeUrlWithPageProtocol(url: string, mode: "http" | "ws"): string {
  if (typeof window === "undefined") return url;
  if (window.location.protocol !== "https:") return url;
  try {
    const u = new URL(url, window.location.href);
    const wantHttp = mode === "http";
    if (u.hostname !== window.location.hostname) return url;
    if (wantHttp && u.protocol === "http:") u.protocol = "https:";
    if (!wantHttp && u.protocol === "ws:") u.protocol = "wss:";
    return u.href.replace(/\/+$/, "");
  } catch {
    return url;
  }
}

function isLikelyLocalDevHostname(hostname: string): boolean {
  const h = String(hostname || "").toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".local");
}

/** 흔한 오타: `riosgames.com` → 실제 도메인 `roisgames.com` */
function fixRiosgamesTypoInUrl(url: string): string {
  try {
    const u = new URL(url);
    if (/\briosgames\.com$/i.test(u.hostname)) {
      u.hostname = u.hostname.replace(/\briosgames\.com$/i, "roisgames.com");
      return u.href.replace(/\/+$/, "");
    }
  } catch {
    // ignore
  }
  return url;
}

/**
 * 게임 BE HTTP 베이스 (보통 …/api). TCP 지연 측정용 health 요청에도 사용
 * - 브라우저에서 env URL 이 **페이지와 다른 호스트**이면(운영에서 CORS 미설정 시)
 *   nginx 등으로 묶인 **동일 출처 `/api`** 를 쓴다. 로컬(127/localhost 등)끼리는 env 그대로 둔다.
 */
function resolveBeHttpBaseFromEnv(envHttp: string | undefined): string {
  const envFixed = envHttp ? fixRiosgamesTypoInUrl(envHttp) : envHttp;
  let base: string;
  if (typeof window !== "undefined") {
    if (envFixed) {
      try {
        const pageHost = window.location.hostname.replace(/\briosgames\.com$/i, "roisgames.com");
        const apiHost = new URL(envFixed, window.location.href).hostname;
        const crossProdHost =
          apiHost !== pageHost && !isLikelyLocalDevHostname(apiHost) && !isLikelyLocalDevHostname(pageHost);
        base = crossProdHost
          ? fixRiosgamesTypoInUrl(`${window.location.origin}/api`)
          : envFixed;
      } catch {
        base = envFixed;
      }
    } else {
      base = `${window.location.origin}/api`;
    }
  } else if (envFixed) {
    base = envFixed;
  } else {
    base = "http://127.0.0.1:58002";
  }
  base = base.replace(/\/+$/, "");
  return alignBeUrlWithPageProtocol(base, "http");
}

/** `/games_holdem/*` 에서만 true (홀덤 전용 FE 번들 + 전용 BE 인스턴스 매칭용) */
function isHoldemAppPathClient(): boolean {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname || "";
  return p === "/games_holdem" || p.startsWith("/games_holdem/");
}

/**
 * 게임 BE HTTP 베이스.
 * `NEXT_PUBLIC_BE_HTTP_HOLDEM` 이 설정된 경우 브라우저에서 `/games_holdem/*` 일 때 그쪽을 사용한다
 * (홀덤 전용 BE 프로세스와 FE 진입 경로를 맞춤). SSR 시에는 `NEXT_PUBLIC_BE_HTTP` 만 사용한다.
 */
export function getBeHttpBase(): string {
  const holdemDedicated = process.env.NEXT_PUBLIC_BE_HTTP_HOLDEM;
  if (holdemDedicated && isHoldemAppPathClient()) {
    return resolveBeHttpBaseFromEnv(holdemDedicated);
  }
  return resolveBeHttpBaseFromEnv(process.env.NEXT_PUBLIC_BE_HTTP);
}

/**
 * BE는 섯다 `/ws` vs 홀덤 `/ws-holdem` 이 분리되어 있다.
 * `NEXT_PUBLIC_BE_WS`만 `…/ws` 로 잡혀 있으면 홀덤도 같은 URL로 붙어 rooms_holdem 이 안 보인다.
 */
function toHoldemWsUrlFromSeotdaEnv(seotdaWsUrl: string): string {
  try {
    const u = new URL(seotdaWsUrl);
    const path = (u.pathname || "/").replace(/\/+$/, "") || "/";
    if (path === "/ws-holdem" || path.endsWith("/ws-holdem")) {
      return seotdaWsUrl;
    }
    if (path === "/ws" || /\/ws$/i.test(path)) {
      u.pathname = path.replace(/\/ws$/i, "/ws-holdem");
      return u.href.replace(/\/+$/, "");
    }
    if (path === "/") {
      u.pathname = "/ws-holdem";
      return u.href.replace(/\/+$/, "");
    }
    u.pathname = `${path}/ws-holdem`;
    return u.href.replace(/\/+$/, "");
  } catch {
    return seotdaWsUrl;
  }
}

export function getBeWsUrl(gameType?: "SEOTDA" | "HOLDEM") {
  let url: string;
  if (gameType === "HOLDEM" && process.env.NEXT_PUBLIC_BE_WS_HOLDEM) {
    url = fixRiosgamesTypoInUrl(process.env.NEXT_PUBLIC_BE_WS_HOLDEM);
  } else if (process.env.NEXT_PUBLIC_BE_WS) {
    url = fixRiosgamesTypoInUrl(process.env.NEXT_PUBLIC_BE_WS);
    if (gameType === "HOLDEM") {
      url = toHoldemWsUrlFromSeotdaEnv(url);
    }
  } else if (typeof window !== "undefined") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    // In deployed environments BE is usually reverse-proxied under /api.
    // Fallback should target /api/ws* so it reaches backend instead of frontend app routes.
    const path = gameType === "HOLDEM" ? "/api/ws-holdem" : "/api/ws";
    const hostname = window.location.hostname.replace(/\briosgames\.com$/i, "roisgames.com");
    const port = window.location.port ? `:${window.location.port}` : "";
    url = `${protocol}//${hostname}${port}${path}`;
  } else {
    url = gameType === "HOLDEM" ? "ws://127.0.0.1:58002/ws-holdem" : "ws://127.0.0.1:58002/ws";
  }
  return alignBeUrlWithPageProtocol(fixRiosgamesTypoInUrl(url), "ws");
}

import { clearLocalAuthSession, getStoredToken, getStoredUserId, getStoredUsername, TOKEN_KEY, USER_ID_KEY, USERNAME_KEY } from "@/lib/session/clientAuth";
import { wsDisconnectAll } from "@/lib/ws/wsSingleton";

export { getStoredToken, getStoredUserId, getStoredUsername };

export function setSession(params: { token: string; userId: string; username: string }) {
  localStorage.setItem(TOKEN_KEY, params.token);
  localStorage.setItem(USER_ID_KEY, params.userId);
  localStorage.setItem(USERNAME_KEY, params.username);
  document.cookie = "isLoggedIn=true; path=/";
}

export function clearSession() {
  clearLocalAuthSession();
  wsDisconnectAll();
}

export function isAdminUser(u: { username?: string; permission?: string } | null | undefined) {
  const perm = String(u?.permission || "").toLowerCase();
  if (perm === "admin") return true;
  const name = String(u?.username || "").toLowerCase();
  if (name === "superadmin" || name === "godview") return true;
  if (name.startsWith("admin_")) return true;
  return false;
}

/** Game BE /auth/login 호출 → 로그인 성공 시 BE에서 DB(loginlogs) 기록 */
export async function beLogin(input: { username: string; password: string }): Promise<BeLoginResponse> {
  const res = await fetch(`${getBeHttpBase()}/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return (await res.json()) as BeLoginResponse;
}

export async function beRegister(input: {
  username: string;
  password: string;
  agent: string;
  usdtWalletAddress?: string;
}): Promise<BeRegisterResponse> {
  const res = await fetch(`${getBeHttpBase()}/auth/register`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return (await res.json()) as BeRegisterResponse;
}

export type BeMeResponse =
  | {
      ok: true;
      user: {
        id: string;
        username: string;
        nickname?: string;
        gender?: "M" | "F";
        permission?: "user" | "admin" | "agent";
        usdtWalletAddress?: string;
        balance: number;
        /** 에이전트 전용: 레이크 적립 포인트 */
        point?: number;
        characterImageNo?: number;
        agent?: string;
        /** 홀덤 9맥 프로필 카드 드래그 오프셋 (줄 단위 텍스트) */
        layoutSetting?: string;
      };
    }
  | { ok: false; error: string; message?: string };

export async function beGetMe(token: string): Promise<BeMeResponse> {
  // Use /auth 영역(로그인과 동일 라우팅) - nginx/도커 변경 없이 동작
  const res = await fetch(`${getBeHttpBase()}/auth/me`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as BeMeResponse;
}

/** 게이트웨이·구 프록시 호환: 짧은 URL 을 먼저 시도 (BE 에 동일 핸들러 마운트). */
const HOLDEM_LAYOUT_SETTING_HTTP_PATHS = ["/auth/layout-setting", "/auth/holdem-layout-setting"] as const;
const HOLDEM_LAYOUT_SETTING_V2_HTTP_PATHS = ["/auth/layout-setting-v2", "/auth/holdem-layout-setting-v2"] as const;

async function tryGetHoldemLayoutFromPublicEndpoints(
  base: string
): Promise<{ value: string; globalAbsolute?: string; valueV2?: string } | null> {
  for (const path of HOLDEM_LAYOUT_SETTING_HTTP_PATHS) {
    try {
      const res = await fetch(`${base}${path}`, { method: "GET" });
      if (!res.ok) continue;
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) continue;
      try {
        const j = (await res.json()) as unknown;
        if (
          j &&
          typeof j === "object" &&
          (j as { ok?: unknown }).ok === true &&
          typeof (j as { value?: unknown }).value === "string"
        ) {
          const value = (j as { value: string }).value;
          const ga = (j as { globalAbsolute?: unknown }).globalAbsolute;
          const v2 = (j as { valueV2?: unknown }).valueV2;
          const out: { value: string; globalAbsolute?: string; valueV2?: string } = { value };
          if (typeof ga === "string" && ga.trim().length > 0) out.globalAbsolute = ga.trim();
          if (typeof v2 === "string" && v2.trim().length > 0) out.valueV2 = v2.trim();
          return out;
        }
      } catch {
        /* next path */
      }
    } catch {
      /* next path */
    }
  }
  return null;
}

/** DB `layoutSetting.__global__` + 선택 `globalAbsolute` / `valueV2` — 공개 GET */
export type BeHoldemLayoutSettingResponse =
  | { ok: true; value: string; globalAbsolute?: string; valueV2?: string }
  | { ok: false; error?: string };

/** DB `layoutSetting` — 공개 GET (`/auth/layout-setting` → …/holdem-layout-setting). `/auth/me`·프로필 필드 사용 안 함. */
export async function beGetHoldemLayoutSetting(_authToken?: string | null): Promise<BeHoldemLayoutSettingResponse> {
  const base = getBeHttpBase();
  const fromPublic = await tryGetHoldemLayoutFromPublicEndpoints(base);
  if (fromPublic !== null) return { ok: true, ...fromPublic };
  return { ok: false, error: "layout-setting unavailable" };
}

/** DB `layoutSetting.globalAbsolute` 만 — 공개 GET (`/auth/holdem-layout-global-absolute`). */
export async function beGetHoldemLayoutGlobalAbsolute(): Promise<
  { ok: true; value: string } | { ok: false; error?: string }
> {
  const base = getBeHttpBase();
  try {
    const res = await fetch(`${base}/auth/holdem-layout-global-absolute`, { method: "GET" });
    if (!res.ok) return { ok: false, error: "NOT_OK" };
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (!ct.includes("application/json")) return { ok: false, error: "NOT_JSON" };
    const j = (await res.json()) as unknown;
    if (
      j &&
      typeof j === "object" &&
      (j as { ok?: unknown }).ok === true &&
      typeof (j as { value?: unknown }).value === "string"
    ) {
      return { ok: true, value: (j as { value: string }).value };
    }
  } catch {
    /* ignore */
  }
  return { ok: false, error: "global-absolute unavailable" };
}

/**
 * 홀덤 레이아웃을 `POST /auth/layout-setting` (별칭) 또는 `…/holdem-layout-setting` 으로 저장.
 * `value` → `__global__`(선택), `globalAbsolute` → `_id: globalAbsolute`(선택) — 최소 하나는 비어 있지 않아야 함.
 */
export async function beSaveHoldemLayoutSetting(
  token: string,
  value: string | null | undefined,
  globalAbsolute?: string | null
): Promise<{ ok: true; value: string; globalAbsolute?: string } | { ok: false; error: string }> {
  const base = getBeHttpBase();
  const body: { value?: string; globalAbsolute?: string } = {};
  const val = typeof value === "string" ? value.trim() : "";
  if (val.length > 0) body.value = val;
  if (typeof globalAbsolute === "string" && globalAbsolute.trim().length > 0) {
    body.globalAbsolute = globalAbsolute.trim();
  }
  if (!body.value && !body.globalAbsolute) {
    return { ok: false, error: "EMPTY_BODY" };
  }
  for (const path of HOLDEM_LAYOUT_SETTING_HTTP_PATHS) {
    try {
      const res = await fetch(`${base}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!res.ok) continue;
      if (!ct.includes("application/json")) continue;
      try {
        const j = (await res.json()) as unknown;
        if (
          j &&
          typeof j === "object" &&
          (j as { ok?: unknown }).ok === true &&
          typeof (j as { value?: unknown }).value === "string"
        ) {
          const out: { ok: true; value: string; globalAbsolute?: string } = {
            ok: true,
            value: (j as { value: string }).value,
          };
          const ga = (j as { globalAbsolute?: unknown }).globalAbsolute;
          if (typeof ga === "string" && ga.trim().length > 0) out.globalAbsolute = ga.trim();
          return out;
        }
      } catch {
        /* try next path */
      }
    } catch {
      /* try next path */
    }
  }
  return { ok: false, error: "SAVE_FAILED" };
}

export type BeHoldemLayoutSettingV2SaveResponse =
  | { ok: true; value: string; valueV2?: string; globalAbsolute?: string }
  | { ok: false; error: string };

/**
 * LSAVE2 — `POST /auth/layout-setting-v2` (별칭 `…/holdem-layout-setting-v2`).
 * `valueV2` → `__v2__`; 선택 `value` → `__global__`, `globalAbsolute` → `globalAbsolute` 문서.
 */
export async function beSaveHoldemLayoutSettingV2(
  token: string,
  params: { valueV2: string; value?: string; globalAbsolute?: string | null }
): Promise<BeHoldemLayoutSettingV2SaveResponse> {
  const base = getBeHttpBase();
  const body: { valueV2: string; value?: string; globalAbsolute?: string } = {
    valueV2: params.valueV2,
  };
  const leg = typeof params.value === "string" ? params.value.trim() : "";
  if (leg.length > 0) body.value = leg;
  const ga = typeof params.globalAbsolute === "string" ? params.globalAbsolute.trim() : "";
  if (ga.length > 0) body.globalAbsolute = ga;
  for (const path of HOLDEM_LAYOUT_SETTING_V2_HTTP_PATHS) {
    try {
      const res = await fetch(`${base}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!res.ok) continue;
      if (!ct.includes("application/json")) continue;
      try {
        const j = (await res.json()) as unknown;
        if (
          j &&
          typeof j === "object" &&
          (j as { ok?: unknown }).ok === true &&
          typeof (j as { value?: unknown }).value === "string"
        ) {
          const out: { ok: true; value: string; valueV2?: string; globalAbsolute?: string } = {
            ok: true,
            value: (j as { value: string }).value,
          };
          const v2 = (j as { valueV2?: unknown }).valueV2;
          if (typeof v2 === "string" && v2.trim().length > 0) out.valueV2 = v2.trim();
          const gaO = (j as { globalAbsolute?: unknown }).globalAbsolute;
          if (typeof gaO === "string" && gaO.trim().length > 0) out.globalAbsolute = gaO.trim();
          return out;
        }
      } catch {
        /* try next path */
      }
    } catch {
      /* try next path */
    }
  }
  return { ok: false, error: "SAVE_V2_FAILED" };
}

/** 에이전트 전용: 포인트 전액을 잔액(머니)으로 전환 */
export async function beConvertPoint(token: string): Promise<{ ok: true; data: { balance: number; point: number } } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/convert-point`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as { ok: true; data: { balance: number; point: number } } | { ok: false; error: string };
}

export async function beUpdateMyProfile(
  token: string,
  input: {
    nickname?: string;
    gender?: "M" | "F";
    usdtWalletAddress?: string;
    characterImageNo?: number;
  }
): Promise<BeMeResponse> {
  const res = await fetch(`${getBeHttpBase()}/auth/profile`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(input || {}),
  });
  return (await res.json()) as BeMeResponse;
}

export type NoticeItem = {
  _id: string;
  type: "NOTICE";
  title: string;
  content: string;
  isImportant: boolean;
  createdBy: { id: string; username: string };
  createdAt: string;
  updatedAt: string;
};

export type InquiryItem = {
  _id: string;
  type: "INQUIRY";
  title: string;
  content: string;
  isImportant?: boolean;
  userId?: string;
  status?: "OPEN" | "ANSWERED";
  answer?: string;
  answeredAt?: string;
  createdBy: { id: string; username: string };
  createdAt: string;
  updatedAt: string;
};

export async function beListNotices(token: string): Promise<{ ok: true; items: NoticeItem[] } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/notices`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

export async function beCreateNotice(
  token: string,
  input: { title: string; content: string; isImportant?: boolean }
): Promise<{ ok: true; item: NoticeItem } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/notices`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
  return (await res.json()) as any;
}

export async function beUpdateNotice(
  token: string,
  id: string,
  input: { title?: string; content?: string; isImportant?: boolean }
): Promise<{ ok: true; item: NoticeItem | null } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/notices/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
  return (await res.json()) as any;
}

export async function beDeleteNotice(token: string, id: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/notices/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

export async function beListInquiries(token: string): Promise<{ ok: true; items: InquiryItem[] } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/inquiries`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

export async function beCreateInquiry(
  token: string,
  input: { title: string; content: string }
): Promise<{ ok: true; item: InquiryItem } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/inquiries`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
  return (await res.json()) as any;
}

export async function beUpdateInquiry(
  token: string,
  id: string,
  input: { title?: string; content?: string; answer?: string; status?: "OPEN" | "ANSWERED" }
): Promise<{ ok: true; item: InquiryItem | null } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/inquiries/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
  return (await res.json()) as any;
}

export async function beDeleteInquiry(token: string, id: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`${getBeHttpBase()}/auth/inquiries/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

// Deposit (charge) request
export type DepositTransactionItem = {
  _id: string;
  amount: number;
  creditedToSiteBalance?: number;
  rake?: { rate: number; amount: number };
  status: "PENDING" | "APPROVED" | "REJECTED";
  createdAt: string;
  confirmedAt?: string | null;
  /** 입금 신청 시 사용자가 입력한 송금 지갑 */
  senderWalletAddress?: string;
  /** 입금 신청 시점의 시스템(플랫폼) 입금 USDT 주소 스냅샷 */
  depositAddress?: string;
  /** 입금 신청 시점 회원 프로필 USDT 주소 스냅샷 */
  userProfileUsdtAddressAtDeposit?: string;
};

export async function beDepositRequest(
  token: string,
  params: { amount: number; senderWalletAddress?: string }
): Promise<
  | { ok: true; transaction: DepositTransactionItem }
  | { ok: false; error: string; message?: string }
> {
  const res = await fetch(`${getBeHttpBase()}/auth/deposit-request`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify({
      amount: params.amount,
      senderWalletAddress: String(params.senderWalletAddress ?? "").trim(),
    }),
  });
  return (await res.json()) as any;
}

export async function beWithdrawalRequest(
  token: string,
  params: { amount: number; usdtAddress: string }
): Promise<
  | { ok: true; transaction: { _id: string; amount: number; withdrawalAddress?: string; status: string; createdAt: string } }
  | { ok: false; error: string; message?: string }
> {
  const res = await fetch(`${getBeHttpBase()}/auth/withdrawal-request`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify({ amount: params.amount, usdtAddress: params.usdtAddress.trim() }),
  });
  return (await res.json()) as any;
}

export type TransactionItem = DepositTransactionItem & { type?: string; withdrawalAddress?: string };

export async function beListMyTransactions(
  token: string,
  options?: { limit?: number; status?: "PENDING" | "APPROVED" | "REJECTED"; type?: "DEPOSIT" | "WITHDRAWAL" | "ALL" }
): Promise<{ ok: true; items: TransactionItem[] } | { ok: false; error: string }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.status) params.set("status", options.status);
  if (options?.type) params.set("type", options.type);
  const q = params.toString();
  const res = await fetch(`${getBeHttpBase()}/auth/my-transactions${q ? `?${q}` : ""}`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

export type GameHistoryItem = {
  gameRoundId: string;
  tableId: string;
  roomId: string;
  roomName: string;
  roundId: number;
  profit: number;
  isWinner: boolean;
  endedAt: string;
  myHandLabel: string | null;
  myBestCards: string[];
  winnerName: string | null;
  winnerHandLabel: string | null;
  winnerBestCards: string[];
};

export async function beGetAgentUserTree(token: string): Promise<
  | { ok: true; data: any[] }
  | { ok: false; error: string }
> {
  const res = await fetch(`${getBeHttpBase()}/auth/agent-user-tree`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

export async function beListMyGameHistory(
  token: string,
  options?: { limit?: number }
): Promise<{ ok: true; items: GameHistoryItem[] } | { ok: false; error: string }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit ?? 20));
  const res = await fetch(`${getBeHttpBase()}/auth/my-game-history?${params}`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

export type GameMoneyTxItem = {
  _id: string;
  type: string;
  amount: number;
  creditedToSiteBalance?: number;
  status?: string;
  createdAt: string;
  confirmedAt?: string;
  settlementDetail?: {
    sessionId?: string;
    tableId?: string;
    roundId?: number;
    username?: string;
    ante?: number;
    bet?: number;
    delta?: number;
    balanceBefore?: number;
    balanceAfter?: number;
  };
  externalRef?: { providerRef?: string };
};

/** 게임 라운드 정산 머니 이력 (transactionsGame) — 입출금과 분리 */
export async function beListMyTransactionsGame(
  token: string,
  options?: { limit?: number; startDate?: string; endDate?: string }
): Promise<{ ok: true; items: GameMoneyTxItem[] } | { ok: false; error: string }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.startDate) params.set("startDate", options.startDate);
  if (options?.endDate) params.set("endDate", options.endDate);
  const res = await fetch(`${getBeHttpBase()}/auth/my-transactions-game?${params}`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

export type RoomHistoryItem = {
  id: string;
  at: number;
  roundId: number;
  kind: "WIN" | "REPLAY" | "TIE";
  winnerName?: string;
  handLabel?: string;
  cards?: string[];
  potGross?: number;
  feeRate?: number;
  feeMoney?: number;
  potWon?: number;
  antePerPlayer?: number;
  anteTotal?: number;
  deltasList?: Array<{ userId: string; username: string; delta: number }>;
};

export async function beListRoomHistory(
  token: string,
  params: { tableId: string; date: string; limit?: number }
): Promise<{ ok: true; items: RoomHistoryItem[] } | { ok: false; error: string }> {
  const sp = new URLSearchParams();
  sp.set("tableId", params.tableId);
  sp.set("date", params.date);
  if (params.limit) sp.set("limit", String(params.limit));
  const res = await fetch(`${getBeHttpBase()}/auth/room-history?${sp}`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json()) as any;
}

